<template>
  <pui-file-upload-dialog
    ref="dialog"
    :canImport="canImport"
    :showError="hasTreeError"
    :reloadAfterError="reloadAfterError"
    @changeFiles="onChangeFiles"
    @import="onImport"
    @closed="resetState"
    @canShowMainContent="onShowMainContent"
    class="dam-file-upload-dialog"
  >
    <template #form>
      <pui-folder-tree
        ref="folderTree"
        v-scrollable.y
        :options="{
          checkbox: false,
          autoExpandRoots: true,
        }"
        @selectionChange="onFolderSelectionChange"
        :getRoots="getUploadRootFolders"
        @loadedFolders="onLoadedFolders"
        class="upload-tree"
      >
        <template #nodeNameLabel="{ node }">
          <pui-folder-breadcrumbs
            v-if="node._showAncestorsPath"
            :lastN="-1"
            :path="node.paths && node.paths.length ? node.paths[0] : ''"
            :finalLabel="piivoTranslate(node)"
          ></pui-folder-breadcrumbs>
          <template v-else> {{ node | piivoTranslate }}</template>
        </template>
      </pui-folder-tree></template
    >
  </pui-file-upload-dialog>
</template>

<script>
import Vue from 'vue';

import services from '../../../../core/services';
import { CHECKED_STATES, SelectionState } from '../../constants/folderTree';
import { DAM_FILE_UPLOAD_DEFAULT_TASK } from '../../constants/file';
import { getUploadRootFolders } from '../../helpers/fileHelper';
import { getFolderIdsFromPath } from '../../utils/folders';
import puiFolderBreadcrumbs from './FolderBreadcrumbs';
import puiFolderTree from './FolderTree';
import damFileUploadNotification from '../files/FileUploadNotification.vue';
import { getDamService } from '../../services';

export default {
  components: {
    puiFolderTree,
    puiFolderBreadcrumbs,
  },
  data() {
    return {
      files: [],
      selectedFolder: null,
      // TREE
      hasTreeError: false,
      rootFolders: null,
      /**
       * Array of folder selections that should
       * be used to determine the first automatically
       * selected node
       */
      allFoldersPreselection: [],
      didInitSelection: false,
    };
  },
  computed: {
    /**
     * @returns {boolean} if the user can execute an import
     */
    canImport() {
      return !!this.files.length && !!this.selectedFolder;
    },
  },
  methods: {
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    /**
     * Opens the upload dialog
     *
     * @param {object[]} array of selected folders from which to preselect
     * a folder
     */
    open(allFoldersPreselection) {
      this.$refs.dialog.open();

      this.allFoldersPreselection = allFoldersPreselection;
    },
    /**
     * Removes the error state
     */
    reloadAfterError() {
      // Reset root folders so we will reload them
      this.rootFolders = null;
      // Toggling 'hasTreeError' will show the tree,
      // and it will auto reload on mount
      this.hasTreeError = false;
    },
    /**
     * Initializes the state of the dialog
     * for a new import
     */
    resetState() {
      this.files = [];
      this.selectedFolder = null;
      this.didInitSelection = false;
    },
    /**
     * Called when the array of files to upload changes
     */
    onChangeFiles(files) {
      this.files = files;
      // TODO: when dam can use a form for uploads: verify form is valid
    },
    // TREE
    /**
     * Updates the folder tree selection when displayed
     *
     * @param {boolean} nextValue - the next value
     */
    onShowMainContent(willShow) {
      // If we start showing main content, we have to re apply
      // the folder selection to the tree
      if (willShow && this.selectedFolder) {
        // Wait for tree to render
        this.$nextTick(() => {
          this.$refs.folderTree.setNodeIdSelectionState(
            this.selectedFolder.itemId,
            SelectionState.MeOnly
          );
        });
      }
    },
    /**
     * Updates the selected folder
     *
     * @param {object[]} folderSelections - the folder selections
     */
    onFolderSelectionChange(folderSelections) {
      // Find the first selected node
      const checkedSelection = folderSelections.find(selection =>
        CHECKED_STATES.includes(selection.state)
      );
      // Set it as the selected folder
      this.selectedFolder = checkedSelection ? checkedSelection.node : null;
    },
    // IMPORT
    /**
     * Triggers an import
     */
    onImport() {
      const fileBlobs = this.files.map(file => file.blob);

      const selectedFolder = {};
      if (this.selectedFolder) {
        selectedFolder.itemId = this.selectedFolder.itemId;
        selectedFolder.label = { ...this.selectedFolder.label };
        selectedFolder.paths = [...this.selectedFolder.paths];
      }

      services
        .getService('uploadManager')
        .uploadFiles(fileBlobs, { folderId: selectedFolder.itemId }, DAM_FILE_UPLOAD_DEFAULT_TASK, {
          notificationComponent: damFileUploadNotification,
          extras: {
            targetFolder: selectedFolder,
          },
        });

      this.$refs.dialog.close();
      this.resetState();
    },
    /**
     * Retrieves the root folders. If the app has specific upload folders configured
     * we will retrieve each of the folders manually, and then use them as roots
     * for the upload tree.
     * If no root folders are configured, simply retrieve the root folders
     * and pass them to the upload tree.
     *
     * @returns {object[]} array of roots for the upload tree
     */
    async getUploadRootFolders() {
      if (this.rootFolders) {
        return this.rootFolders;
      }

      const confFolders = getUploadRootFolders();
      if (Array.isArray(confFolders) && confFolders.length) {
        // Attempt to use specific folders
        const folderPromises = confFolders.map(async confFolder => {
          try {
            const res = await getDamService('foldersService').getFolderByAlias(
              'fileUploadDialog',
              confFolder.alias
            );
            const folder = res.body;

            return {
              ...folder,
              _showAncestorsPath: confFolder.showAncestorsPath,
            };
          } catch (err) {
            // Ignore single errored folders
            return null;
          }
        });

        // Store the roots for future access. Filter out errored folders
        this.rootFolders = (await Promise.all(folderPromises)).filter(Boolean);
        // If no folders could be loaded, show the error
        if (confFolders.length > 0 && this.rootFolders.length === 0) {
          this.hasTreeError = true;
        }
      } else {
        // Simply use roots
        try {
          const res = await getDamService('foldersService').getRootFolders('fileUploadDialog');
          // Store the roots for future access
          this.rootFolders = res.data.items;
        } catch (err) {
          this.hasTreeError = true;
        }
      }

      return this.rootFolders;
    },
    /**
     * Selects the first root in the tree that corresponds to the first
     * preselected folder from 'allFoldersPreselection'
     */
    async onLoadedFolders() {
      if (this.didInitSelection) {
        return;
      }

      this.didInitSelection = true;

      // The first 'loadedFolders' event will be the root folders of the tree
      // That is enough to init the selection

      // Find the first selected node whose path contains one of our roots
      const firstCheckedRootChildSelection = this.allFoldersPreselection.find(selection => {
        if (!CHECKED_STATES.includes(selection.state) || !selection.node.paths.length) {
          return false;
        }

        const folderPathIds = getFolderIdsFromPath(selection.node.paths[0]);
        return folderPathIds.some(folderId =>
          this.rootFolders.find(rootFolder => rootFolder.itemId === folderId)
        );
      });

      const folderToSelect = firstCheckedRootChildSelection
        ? firstCheckedRootChildSelection.node
        : null;

      if (!folderToSelect) {
        return;
      }

      // Wait for dialog to open and tree to render
      await this.$nextTick();
      this.selectedFolder = folderToSelect;
      if (this.$refs.folderTree) {
        this.$refs.folderTree.setNodeIdSelectionState(folderToSelect.itemId, SelectionState.MeOnly);
      }
    },
  },
};
</script>
